import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/layouts/MainLayout';
import {
  CargoDetailsView,
  CargoDetailsSkeleton,
  UpdateCargoModal,
  AddItineraryModal,
  UpdateItineraryModal,
} from '@/components/cargoList';
import { useCargoDetailsStore, useCargoListStore } from '@/store';
import { Alert, Button, DeleteConfirmModal } from '@/components/ui';
import { getWriteErrorMessage } from '@/utils/apiError';
import type { ItineraryItem } from '@/types/cargoList.types';
import { ArrowLeft } from 'lucide-react';

export function CargoDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isAddItineraryModalOpen, setIsAddItineraryModalOpen] = useState(false);
  const [isUpdateItineraryModalOpen, setIsUpdateItineraryModalOpen] = useState(false);
  const [selectedItineraryItem, setSelectedItineraryItem] = useState<ItineraryItem | null>(null);

  const {
    cargo,
    itinerary,
    loading: storeLoading,
    error,
    fetchCargoDetails,
    fetchCargoItinerary,
    clearData,
    reorderItineraryItems,
  } = useCargoDetailsStore();

  const { deleteCargo } = useCargoListStore();

  useEffect(() => {
    const cargoId = id ? parseInt(id, 10) : NaN;

    // A non-numeric :id can never resolve. Stop loading so the "not found" state
    // renders - the old guard left the skeleton up forever instead.
    if (isNaN(cargoId)) {
      setLoading(false);
    } else {
      Promise.all([fetchCargoDetails(cargoId), fetchCargoItinerary(cargoId)]).finally(() =>
        setLoading(false)
      );
    }

    return () => {
      clearData();
    };
  }, [id, fetchCargoDetails, fetchCargoItinerary, clearData]);

  const handleBack = () => {
    navigate('/cargo-list');
  };

  const handleEdit = () => {
    setIsUpdateModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleUpdateSuccess = async () => {
    setIsUpdateModalOpen(false);
    // Refresh the cargo details after update
    if (id && cargo) {
      const cargoId = parseInt(id, 10);
      if (!isNaN(cargoId)) {
        await fetchCargoDetails(cargoId);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!cargo) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteCargo(cargo.id);
      // Navigate back to cargo list after successful delete
      navigate('/cargo-list');
    } catch (error) {
      // Stay on the page and say what happened. Navigating away here would
      // report a delete that the API refused.
      setDeleteError(getWriteErrorMessage(error, 'Failed to delete cargo'));
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeleteError(null);
  };

  const handleUpdateCancel = () => {
    setIsUpdateModalOpen(false);
  };

  const handleAddItinerary = () => {
    setIsAddItineraryModalOpen(true);
  };

  const handleAddItinerarySuccess = async () => {
    setIsAddItineraryModalOpen(false);
    // Refresh the itinerary after adding new item
    if (id && cargo) {
      const cargoId = parseInt(id, 10);
      if (!isNaN(cargoId)) {
        await fetchCargoItinerary(cargoId);
      }
    }
  };

  const handleAddItineraryCancel = () => {
    setIsAddItineraryModalOpen(false);
  };

  const handleEditItinerary = (item: ItineraryItem) => {
    setSelectedItineraryItem(item);
    setIsUpdateItineraryModalOpen(true);
  };

  const handleUpdateItinerarySuccess = async () => {
    setIsUpdateItineraryModalOpen(false);
    setSelectedItineraryItem(null);
    // Refresh the itinerary after updating
    if (id && cargo) {
      const cargoId = parseInt(id, 10);
      if (!isNaN(cargoId)) {
        await fetchCargoItinerary(cargoId);
      }
    }
  };

  const handleUpdateItineraryCancel = () => {
    setIsUpdateItineraryModalOpen(false);
    setSelectedItineraryItem(null);
  };

  const handleReorderItinerary = async (oldOrder: number, newOrder: number) => {
    if (!cargo) return;

    // The reorder endpoint expects the itinerary item's typeId (e.g. 3), not
    // the cargo/fixture typeId (e.g. 1 for Spot Voyage). Read it from the moved
    // item.
    const movedItem = itinerary.find((i) => i.order === oldOrder);
    if (!movedItem) {
      console.error('Could not find moved itinerary item with order', oldOrder);
      return;
    }

    try {
      await reorderItineraryItems(cargo.id, movedItem.typeId, oldOrder, newOrder);

      // Refetch to get the latest server state with correct order values
      const cargoId = parseInt(id!, 10);
      if (!isNaN(cargoId)) {
        await fetchCargoItinerary(cargoId);
      }
    } catch (error) {
      // On error, refresh to revert to server state
      console.error('Failed to reorder itinerary:', error);
      const cargoId = parseInt(id!, 10);
      if (!isNaN(cargoId)) {
        await fetchCargoItinerary(cargoId);
      }
    }
  };

  if (loading || storeLoading) {
    return (
      <MainLayout>
        <div className="px-4">
          <CargoDetailsSkeleton />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="space-y-6 px-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to Cargo List
            </Button>
          </div>
          <Alert variant="error" title="Error Loading Cargo Details">
            <p>{error}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.reload()}
              className="mt-2"
            >
              Try Again
            </Button>
          </Alert>
        </div>
      </MainLayout>
    );
  }

  if (!cargo) {
    return (
      <MainLayout>
        <div className="space-y-6 px-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to Cargo List
            </Button>
          </div>
          <Alert variant="error" title="Cargo Not Found">
            <p>The cargo with ID {id} was not found.</p>
            <Button variant="primary" onClick={handleBack} className="mt-2">
              Back to Cargo List
            </Button>
          </Alert>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="px-4">
        <CargoDetailsView
          cargo={cargo}
          itinerary={itinerary}
          onBack={handleBack}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAddItinerary={handleAddItinerary}
          onEditItinerary={handleEditItinerary}
          onReorderItinerary={handleReorderItinerary}
        />
      </div>

      {/* Update Modal */}
      {isUpdateModalOpen && cargo && (
        <UpdateCargoModal
          isOpen={isUpdateModalOpen}
          onClose={handleUpdateCancel}
          cargoItem={cargo}
          onSuccess={handleUpdateSuccess}
        />
      )}

      {/* Add Itinerary Modal */}
      {isAddItineraryModalOpen && cargo && (
        <AddItineraryModal
          isOpen={isAddItineraryModalOpen}
          onClose={handleAddItineraryCancel}
          cargoId={cargo.id}
          existingItinerary={itinerary}
          onSuccess={handleAddItinerarySuccess}
        />
      )}

      {/* Update Itinerary Modal */}
      {isUpdateItineraryModalOpen && cargo && selectedItineraryItem && (
        <UpdateItineraryModal
          isOpen={isUpdateItineraryModalOpen}
          onClose={handleUpdateItineraryCancel}
          cargoId={cargo.id}
          typeId={selectedItineraryItem.typeId}
          fixtureId={cargo.id}
          item={selectedItineraryItem}
          onSuccess={handleUpdateItinerarySuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Cargo"
        message="Are you sure you want to delete this cargo item? This action cannot be undone."
        itemName={cargo?.charterer}
        loading={isDeleting}
        error={deleteError}
      />
    </MainLayout>
  );
}
